<template>
  <div
    :style="containerStyle"
    v-bind="attrs"
    ref="domRefText"
    class="clamped-text-container"
  >
    <slot />
  </div>

  <LinkWithIcon v-if="fullTextHeightPx > clampedHeightPx" isDottedUnderline color="blue" @click="isClamped = !isClamped" iconInset="1px auto auto 2px" style="margin-left: -6px">
    <template #icon><IconEmbedded :name="isClamped ? 'caret-bottom_4' : 'caret-top_4'" :size="13" /></template>
    <span v-if="isClamped">Show more</span>
    <span v-else>Show less</span>
  </LinkWithIcon>
</template>

<script setup lang="ts">
import { ref, computed, onUpdated, nextTick, onMounted, inject, Ref, watch, useAttrs } from "vue";

// Components
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import LinkWithIcon from "@components/LinkWithIcon.vue";

defineOptions({
  inheritAttrs: false,
});

const props = withDefaults(
  defineProps<{
    visibleLines: number;
  }>(),
  {
    visibleLines: 3,
  }
);

// Global variables & injections
const screenSizeUpdateKeyThrottled = inject("screenSizeUpdateKeyThrottled") as Ref<symbol>;

// DOM refs
const domRefText = ref<any>(null);

const attrs = useAttrs();

// Is Clamped =================================================================
const isClamped = ref<boolean>(true);

// Define full and clamped height =============================================
const fullTextHeightPx = ref<number | null>(null);
const clampedHeightPx = ref<number | null>(null);

const containerStyle = computed(() => ({
  whiteSpace: "pre-wrap",
  ...(isClamped.value
    ? {
        maxHeight: clampedHeightPx.value ? `${clampedHeightPx.value}px` : undefined,
        overflow: "hidden",
        display: "-webkit-box",
        lineClamp: props.visibleLines,
        WebkitLineClamp: props.visibleLines,
        textOverflow: "ellipsis",
        WebkitBoxOrient: "vertical",
        maskImage: fullTextHeightPx.value != null && clampedHeightPx.value != null && fullTextHeightPx.value > clampedHeightPx.value ? `linear-gradient(180deg, rgba(0, 0, 0, 1) ${100 - 100 / props.visibleLines}%, rgba(0, 0, 0, 0) 100%)` : undefined,
      }
    : {}),
}));

function defineActualAndMaxHeight() {
  const computedStyles = window.getComputedStyle(domRefText.value);
  const lineHeightPx = parseFloat(computedStyles.lineHeight) || parseFloat(computedStyles.fontSize) * 1.2;
  clampedHeightPx.value = lineHeightPx * props.visibleLines;

  const clonedNode = domRefText.value.cloneNode(true);
  // cloneNode copies inline !important clamp styles that beat the reset class below; Safari then measures clamped height and hides "Show more"
  clonedNode.style.setProperty("display", "block", "important");
  clonedNode.style.setProperty("overflow", "visible", "important");
  clonedNode.style.setProperty("max-height", "none", "important");
  clonedNode.style.setProperty("-webkit-line-clamp", "unset", "important");
  clonedNode.style.setProperty("line-clamp", "unset", "important");
  clonedNode.style.setProperty("-webkit-box-orient", "initial", "important");
  clonedNode.style.setProperty("width", `${domRefText.value.getBoundingClientRect().width}px`, "important");
  clonedNode.style.setProperty("max-width", `${domRefText.value.getBoundingClientRect().width}px`, "important");
  clonedNode.style.setProperty("font-family", computedStyles["font-family"], "important");
  clonedNode.style.setProperty("font-size", computedStyles["font-size"], "important");
  clonedNode.style.setProperty("line-height", computedStyles["line-height"], "important");
  clonedNode.classList.add("clamped-text-appended-node-styles-reset-225ca9b7-2f6f-4709-b63e-f506b6655ea5");
  const appendedTextContainer = document.body.appendChild(clonedNode);

  fullTextHeightPx.value = appendedTextContainer.getBoundingClientRect().height;

  appendedTextContainer.remove();
}

onMounted(async () => {
  await nextTick();
  defineActualAndMaxHeight();
});

watch(screenSizeUpdateKeyThrottled, async () => {
  await nextTick();
  defineActualAndMaxHeight();
});

onUpdated(async () => {
  await nextTick();
  defineActualAndMaxHeight();
});
</script>

<style scoped lang="scss">
:global(.clamped-text-appended-node-styles-reset-225ca9b7-2f6f-4709-b63e-f506b6655ea5) {
  height: auto !important;
  max-height: unset !important;
  display: block !important;
  position: absolute !important;
  inset: 0 auto auto 0 !important;
  z-index: -100 !important;
  text-overflow: unset !important;
  line-clamp: none !important;
  -webkit-line-clamp: none !important;
  -webkit-box-orient: vertical !important;
  opacity: 0 !important;
  pointer-events: none !important;
}

.clamped-text-container {
  content: "";
}
</style>
